// src/components/reports/ModeratedContentList.jsx
import React from 'react';
import { AlertTriangle /* other icons */ } from 'lucide-react';
import StatusBadge from './StatusBadge';

const ModeratedContentList = ({ 
  items = [], 
  loading = false,
  contentType = 'report', // 'report', 'goget', 'message'
  onReviewClick,
  onSelectionChange, 
  selectedItems = []
}) => {
  // Conditional rendering based on content type
  const getColumns = () => {
    const baseColumns = [
      { id: 'status', label: 'Status', sortable: true },
      { id: 'created', label: 'Time', sortable: true },
      { id: 'actions', label: 'Actions', sortable: false }
    ];
    
    // Add content-specific columns
    switch(contentType) {
      case 'goget':
        return [
          { id: 'content', label: 'GoGet', sortable: true },
          { id: 'reason', label: 'Flags', sortable: true },
          { id: 'source', label: 'Source', sortable: true },
          ...baseColumns
        ];
      case 'message':
        return [
          { id: 'content', label: 'Message', sortable: true },
          { id: 'sender', label: 'Sender', sortable: true },
          { id: 'severity', label: 'Severity', sortable: true },
          ...baseColumns
        ];
      default:
        return [
          { id: 'content', label: 'Content', sortable: true },
          { id: 'type', label: 'Type', sortable: true },
          { id: 'reason', label: 'Reason', sortable: true },
          ...baseColumns
        ];
    }
  };

  // Content cell renderer
  const renderContentCell = (item) => {
    switch(contentType) {
      case 'goget':
        return (
          <div className="flex flex-col">
            <div className="font-medium text-gray-900">{item.content?.itemName || 'Unnamed GoGet'}</div>
            <div className="text-sm text-gray-500 truncate max-w-md">{item.content?.itemDescription || 'No description'}</div>
          </div>
        );
      case 'message':
        return (
          <div className="text-sm text-gray-900 truncate max-w-md">
            {item.content || item.contentId?.content || 'No content available'}
          </div>
        );
      default:
        return (
          <div className="flex flex-col">
            <div className="font-medium text-gray-900">{item.contentType || 'Unknown'}</div>
            <div className="text-sm text-gray-500 truncate max-w-md">{item.details || 'No details'}</div>
          </div>
        );
    }
  };

  // Severity cell renderer
  const renderSeverityCell = (item) => {
    const severity = item.severity || item.flags?.severity || 'low';
    const severityColors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityColors[severity] || severityColors.low}`}>
        <AlertTriangle className="h-3 w-3 mr-1" />
        {severity.charAt(0).toUpperCase() + severity.slice(1)}
      </span>
    );
  };

  // Action cell renderer - Add this if needed
  const renderActionCell = (item) => {
    return (
      <button
        onClick={() => onReviewClick(item._id)}
        className="text-blue-600 hover:text-blue-800 font-medium"
      >
        Review
      </button>
    );
  };

  // Different cell renderers based on content type
  const renderCell = (item, columnId) => {
    switch(columnId) {
      case 'content':
        return renderContentCell(item);
      case 'status':
        return <StatusBadge status={item.status} />;
      case 'severity':
        return renderSeverityCell(item);
      case 'actions':
        return renderActionCell(item);
      case 'created':
        return formatDate(item.createdAt || item.timestamp);
      case 'source':
        return item.source === 'automated' ? 'Automated' : 'User Report';
      case 'type':
        return item.contentType;
      case 'reason':
        return item.reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      case 'sender':
        return item.sender?.username || 'Unknown';
      default:
        // Handle any other column ID or return a fallback
        return <span>{item[columnId] || '-'}</span>;
    }
  };
  
  // Helper function to format dates
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Filter/search controls */}
      
      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {getColumns().map(column => (
                  <th key={column.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={getColumns().length} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={getColumns().length} className="px-6 py-4 text-center text-gray-500">
                    No items found
                  </td>
                </tr>
              ) : (
                items.map(item => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    {getColumns().map(column => (
                      <td key={column.id} className="px-6 py-4 whitespace-nowrap">
                        {renderCell(item, column.id)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModeratedContentList;